import intersection from 'lodash/intersection';
import { useQuery } from 'react-query';
import { searchClient } from 'apis/algolia';

import {
  getProductsQueryParams,
  groupAndCountResultsForMultipleWallets,
  not,
} from 'utils/algolia';
import type { IProduct } from 'apis/algolia';
import { mapCategoriesToSubcategories } from 'utils/product';

const ALGOLIA_VENDOR_LIST_INDEX = 'vendors_list_v1';

const PRODUCT_LIMIT_PER_PAGE = 9;

type EmployeeWallet = {
  company_wallet_configuration: {
    blacklisted_vendors: Array<string>;
    categories: Array<{
      id: string;
      name: string;
      subcategories: Array<{
        eligible: boolean;
        value: string;
      }>;
    }>;
  };
};

const getProductIDFacetFilters = (products: Array<IProduct>) =>
  products.map(({ id }) => `id:${not(id)}`);

const getRelatedProductsSubcategories = (
  employee_wallet: EmployeeWallet,
  products: Array<IProduct>
) => {
  const wallet_subcategories = mapCategoriesToSubcategories(
    employee_wallet.company_wallet_configuration.categories
  );
  const product_subcategories = products.flatMap(
    ({ subcategories }) => subcategories
  );
  return intersection(
    wallet_subcategories,
    Array.from(new Set(product_subcategories))
  );
};

const getRelatedSearchProducts = async ({
  country,
  employee_wallets,
  products,
}: {
  employee_wallets: Array<EmployeeWallet>;
  country: string;
  products: Array<IProduct>;
}) => {
  const product_id_facet_filters = getProductIDFacetFilters(products);
  const queries = employee_wallets.map((employee_wallet) => {
    const subcategories = getRelatedProductsSubcategories(
      employee_wallet,
      products
    );
    const params = getProductsQueryParams({
      country,
      employee_wallet,
      facet_filters: product_id_facet_filters,
      subcategories,
    });
    return {
      indexName: ALGOLIA_VENDOR_LIST_INDEX,
      query: '',
      params: {
        hitsPerPage: PRODUCT_LIMIT_PER_PAGE,
        facetFilters: params.facet_filters,
        numericFilters: params.numeric_filters,
      },
    };
  });

  const { results } = await searchClient.multipleQueries<IProduct>(queries);
  const { items } = groupAndCountResultsForMultipleWallets(results);
  return items.slice(0, PRODUCT_LIMIT_PER_PAGE);
};

type UseQueryRelatedSearchProductsArgs = {
  country: string;
  employee_wallets: Array<EmployeeWallet>;
  onError: (error: Error) => void;
  products: Array<IProduct>;
  query: string;
  finished_paginating_results: boolean;
};

const useQueryRelatedSearchProducts = ({
  country,
  employee_wallets,
  onError,
  products,
  query,
  finished_paginating_results,
}: UseQueryRelatedSearchProductsArgs) => {
  const { data, isLoading } = useQuery(
    [
      `getRelatedSearchProducts`,
      { finished_paginating_results, query, country },
    ],
    () =>
      !finished_paginating_results
        ? Promise.resolve([])
        : getRelatedSearchProducts({
            country,
            employee_wallets,
            products,
          }),
    {
      onError,
    }
  );

  return {
    data,
    isLoading,
  };
};

export default useQueryRelatedSearchProducts;
